'use client'

import { useEffect, useRef, useState } from "react";
import styles from './InlineColorPicker.module.css';

export default function InlineColorPicker({ color, onChange, alpha, onAlphaChange, showAlpha = true }) {
  const hueCanvasRef = useRef(null);
  const colorFieldRef = useRef(null);
  const alphaCanvasRef = useRef(null);
  const [hue, setHue] = useState(0);
  // Store circle position separately - only updated by mouse events, never by color calculations
  const [circleX, setCircleX] = useState(1); // 0 = left (0% sat), 1 = right (100% sat)
  const [circleY, setCircleY] = useState(0); // 0 = top (100% light), 1 = bottom (0% light)
  // Alpha is 0..1. If consumer passes `alpha` + `onAlphaChange`, we mirror that
  // value; otherwise we keep an internal value (and changes are local).
  const [internalAlpha, setInternalAlpha] = useState(
    typeof alpha === "number" && Number.isFinite(alpha)
      ? Math.min(1, Math.max(0, alpha))
      : 1
  );
  const effectiveAlpha = typeof alpha === "number" && Number.isFinite(alpha)
    ? Math.min(1, Math.max(0, alpha))
    : internalAlpha;
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const [isDraggingField, setIsDraggingField] = useState(false);
  const [isDraggingAlpha, setIsDraggingAlpha] = useState(false);
  const [colorFormat, setColorFormat] = useState("HEX");
  const [inputValue, setInputValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isInitializedRef = useRef(false);
  const colorUpdateRafRef = useRef(null);
  const pendingColorUpdateRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const dividerRef = useRef(null);

  // Calculate HSL from raw position
  const getSatFromX = (x) => x * 100; // 0-1 -> 0-100%
  const getLightFromY = (y) => (1 - y) * 100; // 0-1 -> 100-0%

  const updateInputValue = (hexColor, alphaOverride) => {
    const alphaForDisplay = typeof alphaOverride === "number"
      ? alphaOverride
      : effectiveAlpha;
    const isFullyOpaque = alphaForDisplay >= 0.9995;
    if (colorFormat === "HEX") {
      if (isFullyOpaque) {
        setInputValue(hexColor);
      } else {
        const aByte = Math.round(alphaForDisplay * 255).toString(16).padStart(2, "0").toUpperCase();
        setInputValue(`${hexColor}${aByte}`);
      }
    } else {
      const rgb = hexToRgb(hexColor);
      if (rgb) {
        if (isFullyOpaque) {
          setInputValue(`${rgb.red}, ${rgb.green}, ${rgb.blue}`);
        } else {
          setInputValue(`${rgb.red}, ${rgb.green}, ${rgb.blue}, ${Number(alphaForDisplay.toFixed(2))}`);
        }
      }
    }
  };

  const commitAlpha = (nextAlpha) => {
    const clamped = Math.min(1, Math.max(0, Number(nextAlpha) || 0));
    setInternalAlpha(clamped);
    onAlphaChange?.(clamped);
    return clamped;
  };

  // Initialize from color prop - only sets initial position, then decouples
  useEffect(() => {
    if (!isInitializedRef.current) {
      const hslValues = hexToHsl(color);
      setHue(hslValues.hue);
      // Set initial circle position from color, but only once
      setCircleX(hslValues.saturation / 100);
      setCircleY(1 - (hslValues.lightness / 100));
      updateInputValue(color);
      isInitializedRef.current = true;
    }
  }, [color]);

  // Re-sync internal alpha from external prop (when consumer is in controlled mode)
  useEffect(() => {
    if (typeof alpha !== "number" || !Number.isFinite(alpha)) return;
    if (isDraggingAlpha) return;
    const clamped = Math.min(1, Math.max(0, alpha));
    setInternalAlpha(clamped);
  }, [alpha, isDraggingAlpha]);

  // Re-sync from color prop when it changes externally (e.g., embedSettings loaded),
  // but do not interfere while the user is actively dragging.
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (isDraggingHue || isDraggingField) return;
    if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) return;

    // If our current local state already produces the incoming hex, skip the
    // round-trip. Otherwise achromatic / edge-of-field colors (white, black,
    // pure greys) snap hue back to 0 and collapse the field circle to one
    // position even though the user's slider position was perfectly valid.
    const localHex = hslToHex(hue, getSatFromX(circleX), getLightFromY(circleY));
    if (localHex.toLowerCase() === color.toLowerCase()) {
      updateInputValue(color);
      return;
    }

    const hslValues = hexToHsl(color);
    setHue(hslValues.hue);
    setCircleX(hslValues.saturation / 100);
    setCircleY(1 - (hslValues.lightness / 100));
    updateInputValue(color);
    // Note: we purposefully do not redraw canvases here; hue change triggers redraw,
    // and the indicator positions are derived from circleX/circleY states.
  }, [color, isDraggingHue, isDraggingField]);

  // Draw canvases whenever component mounts or hue changes
  useEffect(() => {
    drawHueSlider();
    drawColorField();
    drawAlphaSlider();
  }, [hue]);

  // Alpha canvas's gradient depends on the selected solid color (hue + sat + light)
  useEffect(() => {
    drawAlphaSlider();
  }, [hue, circleX, circleY]);

  // Redraw alpha canvas on resize so it stays full-width
  useEffect(() => {
    const canvas = alphaCanvasRef.current;
    const parent = canvas?.parentElement;
    if (!parent) return;

    const handle = () => {
      try { drawAlphaSlider(); } catch {}
    };

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handle) : null;
    if (ro) ro.observe(parent);

    const rafId = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(handle) : null;

    return () => {
      if (ro) ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Redraw color field when its parent resizes (ensures full-width on first layout and resizes)
  useEffect(() => {
    const canvas = colorFieldRef.current;
    const parent = canvas?.parentElement;
    if (!parent) return;

    const handle = () => {
      try { drawColorField(); } catch {}
    };

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handle) : null;
    if (ro) ro.observe(parent);

    // Also attempt a post-layout draw in the next frame to catch first mount sizing
    const rafId = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(handle) : null;

    return () => {
      if (ro) ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [hue]);

  const drawHueSlider = () => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;

    const width = 20;
    const height = 200;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Draw smooth hue gradient from top to bottom (0° to 360°)
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    for (let hueValue = 0; hueValue <= 360; hueValue += 30) {
      const position = hueValue / 360;
      gradient.addColorStop(position, `hsl(${hueValue}, 100%, 50%)`);
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  };

  const drawColorField = () => {
    const canvas = colorFieldRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    const parentWidth = parent ? parent.offsetWidth : 200;
    const width = Number.isFinite(parentWidth) && parentWidth > 0 ? parentWidth : 200;
    const height = 200;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Draw each pixel to create accurate color field
    // X-axis: saturation (0% left, 100% right)
    // Y-axis: lightness (100% top, 0% bottom)
    const safeW = Math.max(1, Math.floor(width));
    const safeH = Math.max(1, Math.floor(height));
    const imageData = ctx.createImageData(safeW, safeH);
    const pixelData = imageData.data;

    for (let y = 0; y < safeH; y++) {
      for (let x = 0; x < safeW; x++) {
        const saturationValue = (x / safeW) * 100; // 0% to 100% left to right
        const lightnessValue = 100 - (y / safeH) * 100; // 100% to 0% top to bottom
        
        const rgb = hslToRgb(hue, saturationValue, lightnessValue);
        const pixelIndex = (y * safeW + x) * 4;
        
        pixelData[pixelIndex] = rgb.red;     // R
        pixelData[pixelIndex + 1] = rgb.green; // G
        pixelData[pixelIndex + 2] = rgb.blue;  // B
        pixelData[pixelIndex + 3] = 255;       // A
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  const drawAlphaSlider = () => {
    const canvas = alphaCanvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const parentWidth = parent ? parent.offsetWidth : 200;
    const width = Number.isFinite(parentWidth) && parentWidth > 0 ? parentWidth : 200;
    const height = 14;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Checkered transparency background
    const checkSize = 6;
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#2e2e2e';
    for (let y = 0; y < height; y += checkSize) {
      const rowOffset = (Math.floor(y / checkSize) % 2 === 0) ? 0 : checkSize;
      for (let x = rowOffset; x < width; x += checkSize * 2) {
        ctx.fillRect(x, y, checkSize, checkSize);
      }
    }

    // Solid color we're sweeping toward — uses current hue/sat/light, NOT alpha
    const sat = getSatFromX(circleX);
    const light = getLightFromY(circleY);
    const rgb = hslToRgb(hue, sat, light);

    // Gradient from transparent (left) to fully opaque (right)
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0)`);
    gradient.addColorStop(1, `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 1)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  };

  const emitColorChange = (targetHue, targetX, targetY, immediate = false) => {
    // Store the latest color update request
    pendingColorUpdateRef.current = { targetHue, targetX, targetY };

    const updateColor = () => {
      const update = pendingColorUpdateRef.current;
      if (!update) {
        colorUpdateRafRef.current = null;
        return;
      }
      
      // Store and clear the pending update
      const currentUpdate = update;
      pendingColorUpdateRef.current = null;
      
      // Calculate HSL from circle position
      const saturationValue = getSatFromX(currentUpdate.targetX);
      const lightnessValue = getLightFromY(currentUpdate.targetY);
      
      // Convert to RGB and round to nearest integer
      const rgb = hslToRgb(currentUpdate.targetHue, saturationValue, lightnessValue);
      const roundedRgb = {
        red: Math.round(rgb.red),
        green: Math.round(rgb.green),
        blue: Math.round(rgb.blue)
      };
      
      // Convert rounded RGB back to HEX
      const hexColor = rgbToHex(roundedRgb.red, roundedRgb.green, roundedRgb.blue);
      updateInputValue(hexColor);
      onChange?.(hexColor);
      // NOTE: We do NOT update circleX/circleY here - they stay where the cursor is
      
      // Check if a new update arrived while we were processing (continues the loop)
      if (pendingColorUpdateRef.current) {
        // Schedule next frame to continue updating
        colorUpdateRafRef.current = requestAnimationFrame(updateColor);
      } else {
        colorUpdateRafRef.current = null;
      }
    };

    // Use requestAnimationFrame for smooth, frequent updates synchronized with browser repaint
    if (immediate) {
      // Cancel any pending RAF and update immediately
      if (colorUpdateRafRef.current) {
        cancelAnimationFrame(colorUpdateRafRef.current);
        colorUpdateRafRef.current = null;
      }
      // Clear pending update and execute immediately
      pendingColorUpdateRef.current = null;
      const update = { targetHue, targetX, targetY };
      const saturationValue = getSatFromX(update.targetX);
      const lightnessValue = getLightFromY(update.targetY);
      const rgb = hslToRgb(update.targetHue, saturationValue, lightnessValue);
      const roundedRgb = {
        red: Math.round(rgb.red),
        green: Math.round(rgb.green),
        blue: Math.round(rgb.blue)
      };
      const hexColor = rgbToHex(roundedRgb.red, roundedRgb.green, roundedRgb.blue);
      updateInputValue(hexColor);
      onChange?.(hexColor);
    } else {
      // Start RAF loop if not already running
      if (!colorUpdateRafRef.current) {
        colorUpdateRafRef.current = requestAnimationFrame(updateColor);
      }
    }
  };

  const updateHueFromEvent = (event) => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const yPos = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    const newHue = (yPos / rect.height) * 360;
    setHue(newHue);
    // Emit color change using current circle position (don't move circle)
    emitColorChange(newHue, circleX, circleY);
  };

  const updateAlphaFromEvent = (event) => {
    const canvas = alphaCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const xPos = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const newAlpha = rect.width > 0 ? xPos / rect.width : 0;
    const clamped = commitAlpha(newAlpha);
    // Refresh the input string so it reflects the new alpha
    const sat = getSatFromX(circleX);
    const light = getLightFromY(circleY);
    const currentHex = hslToHex(hue, sat, light);
    updateInputValue(currentHex, clamped);
  };

  const updateFieldFromEvent = (event) => {
    const canvas = colorFieldRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    
    // Get raw position (0-1) directly from cursor - this is the ONLY source of truth for circle position
    const rawX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const rawY = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    
    // Update circle position immediately - smooth cursor tracking, no throttling
    setCircleX(rawX);
    setCircleY(rawY);
    
    // Emit color change with rounded RGB (throttled) - but circle position stays at exact cursor position
    emitColorChange(hue, rawX, rawY, false);
  };

  // Global pointer handlers to prevent cross-interaction
  useEffect(() => {
    if (!isDraggingHue && !isDraggingField && !isDraggingAlpha) return;

    const handleGlobalPointerMove = (event) => {
      if (isDraggingHue) {
        event.preventDefault();
        const canvas = hueCanvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const yPos = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
        const newHue = (yPos / rect.height) * 360;
        setHue(newHue);
        // Use current circle position, don't move it
        emitColorChange(newHue, circleX, circleY);
      } else if (isDraggingField) {
        event.preventDefault();
        const canvas = colorFieldRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const rawX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        const rawY = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));

        // Update circle position immediately - smooth cursor tracking
        setCircleX(rawX);
        setCircleY(rawY);

        // Emit rounded color (throttled), but circle stays at exact cursor position
        emitColorChange(hue, rawX, rawY, false);
      } else if (isDraggingAlpha) {
        event.preventDefault();
        updateAlphaFromEvent(event);
      }
    };

    const handleGlobalPointerUp = () => {
      // Stop the RAF loop and emit final color immediately when dragging stops
      if (isDraggingField) {
        // Clear pending updates and emit final color
        if (colorUpdateRafRef.current) {
          cancelAnimationFrame(colorUpdateRafRef.current);
          colorUpdateRafRef.current = null;
        }
        emitColorChange(hue, circleX, circleY, true);
        pendingColorUpdateRef.current = null;
      }
      setIsDraggingHue(false);
      setIsDraggingField(false);
      setIsDraggingAlpha(false);
    };

    document.addEventListener('pointermove', handleGlobalPointerMove);
    document.addEventListener('pointerup', handleGlobalPointerUp);
    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
    };
  }, [isDraggingHue, isDraggingField, isDraggingAlpha, circleX, circleY, hue]);

  // Hue slider handlers
  const handleHuePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingHue(true);
    setIsDraggingField(false); // Ensure field dragging is off
    updateHueFromEvent(event);
    try { hueCanvasRef.current?.setPointerCapture?.(event.pointerId); } catch {}
  };

  const handleHuePointerMove = (event) => {
    if (!isDraggingHue) return;
    event.preventDefault();
    event.stopPropagation();
    updateHueFromEvent(event);
  };

  const handleHuePointerUp = (event) => {
    setIsDraggingHue(false);
    try { hueCanvasRef.current?.releasePointerCapture?.(event.pointerId); } catch {}
  };

  // Color field handlers
  const handleFieldPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingField(true);
    setIsDraggingHue(false); // Ensure hue dragging is off
    updateFieldFromEvent(event);
    try { colorFieldRef.current?.setPointerCapture?.(event.pointerId); } catch {}
  };

  const handleFieldPointerMove = (event) => {
    if (!isDraggingField) return;
    event.preventDefault();
    event.stopPropagation();
    updateFieldFromEvent(event);
  };

  const handleFieldPointerUp = (event) => {
    // Stop the RAF loop and emit final color immediately when dragging stops
    if (colorUpdateRafRef.current) {
      cancelAnimationFrame(colorUpdateRafRef.current);
      colorUpdateRafRef.current = null;
    }
    emitColorChange(hue, circleX, circleY, true);
    pendingColorUpdateRef.current = null;
    setIsDraggingField(false);
    try { colorFieldRef.current?.releasePointerCapture?.(event.pointerId); } catch {}
  };

  // Alpha slider handlers
  const handleAlphaPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingAlpha(true);
    setIsDraggingHue(false);
    setIsDraggingField(false);
    updateAlphaFromEvent(event);
    try { alphaCanvasRef.current?.setPointerCapture?.(event.pointerId); } catch {}
  };

  const handleAlphaPointerMove = (event) => {
    if (!isDraggingAlpha) return;
    event.preventDefault();
    event.stopPropagation();
    updateAlphaFromEvent(event);
  };

  const handleAlphaPointerUp = (event) => {
    setIsDraggingAlpha(false);
    try { alphaCanvasRef.current?.releasePointerCapture?.(event.pointerId); } catch {}
  };

  // Input handlers
  const handleInputChange = (value) => {
    setInputValue(value);
    
    if (colorFormat === "HEX") {
      if (/^#[0-9A-Fa-f]{8}$/.test(value)) {
        const hex6 = value.slice(0, 7).toUpperCase();
        const aByte = parseInt(value.slice(7, 9), 16);
        const aValue = Math.max(0, Math.min(1, aByte / 255));
        const hslValues = hexToHsl(hex6);
        if (hslValues.saturation > 0) setHue(hslValues.hue);
        setCircleX(hslValues.saturation / 100);
        setCircleY(1 - (hslValues.lightness / 100));
        commitAlpha(aValue);
        onChange?.(hex6);
      } else if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
        const hslValues = hexToHsl(value);
        // Achromatic colors (sat=0) report hue=0 in hexToHsl. Don't overwrite
        // the user's current hue with a meaningless 0 — keep the slider in place.
        if (hslValues.saturation > 0) setHue(hslValues.hue);
        setCircleX(hslValues.saturation / 100);
        setCircleY(1 - (hslValues.lightness / 100));
        commitAlpha(1);
        onChange?.(value);
      }
    } else {
      const parts = value.split(',').map(part => part.trim());
      if (parts.length === 3 || parts.length === 4) {
        const red = parseInt(parts[0]);
        const green = parseInt(parts[1]);
        const blue = parseInt(parts[2]);
        const aRaw = parts.length === 4 ? Number(parts[3]) : 1;
        const aValue = Number.isFinite(aRaw) ? Math.max(0, Math.min(1, aRaw)) : 1;
        if (red >= 0 && red <= 255 && green >= 0 && green <= 255 && blue >= 0 && blue <= 255) {
          const hex = rgbToHex(red, green, blue);
          const hslValues = hexToHsl(hex);
          if (hslValues.saturation > 0) setHue(hslValues.hue);
          setCircleX(hslValues.saturation / 100);
          setCircleY(1 - (hslValues.lightness / 100));
          commitAlpha(aValue);
          onChange?.(hex);
        }
      }
    }
  };

  // Update input value when format changes or circle moves, but don't update circle position
  useEffect(() => {
    const saturationValue = getSatFromX(circleX);
    const lightnessValue = getLightFromY(circleY);
    const currentHex = hslToHex(hue, saturationValue, lightnessValue);
    updateInputValue(currentHex, effectiveAlpha);
  }, [colorFormat, hue, circleX, circleY, effectiveAlpha]);

  // Set dropdown menu width and position to match button+gap (divider) when dropdown opens
  useEffect(() => {
    if (
      isDropdownOpen &&
      dropdownButtonRef.current &&
      dropdownMenuRef.current &&
      inputWrapperRef.current &&
      dividerRef.current
    ) {
      const buttonWrapperRect = dropdownButtonRef.current.parentElement.getBoundingClientRect();
      const dividerRect = dividerRef.current.getBoundingClientRect();
      const wrapperRect = inputWrapperRef.current.getBoundingClientRect();

      // Width from left edge of button wrapper to right edge of divider (includes the gap)
      const computedWidth = Math.round(dividerRect.right - buttonWrapperRect.left);
      // Expand width by 1px and shift left by -1px to cover container's left border
      dropdownMenuRef.current.style.width = `${computedWidth + 1}px`;

      // Position dropdown: keep left overlay and move down slightly so top border doesn't intrude into input
      dropdownMenuRef.current.style.left = `${buttonWrapperRect.left - wrapperRect.left - 1}px`;
      dropdownMenuRef.current.style.top = `${buttonWrapperRect.bottom - wrapperRect.top + 1}px`;
    }
  }, [isDropdownOpen]);

  // Calculate positions for indicators - use circle position directly (independent of color rounding)
  const fieldXPercent = circleX * 100;
  const fieldYPercent = circleY * 100;
  
  // Get current HSL values for display color (used in circle indicator)
  const currentSaturation = getSatFromX(circleX);
  const currentLightness = getLightFromY(circleY);

  // For the hue slider indicator
  const hueRect = hueCanvasRef.current?.getBoundingClientRect();
  const hueHeight = hueRect?.height || 1;
  const hueYPercent = (hue / 360) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.colorPickerRow}>
        {/* Color Field */}
        <div className={styles.colorFieldWrapper}>
          <canvas
            ref={colorFieldRef}
            onPointerDown={handleFieldPointerDown}
            onPointerMove={handleFieldPointerMove}
            onPointerUp={handleFieldPointerUp}
            className={styles.colorFieldCanvas}
            style={{ 
              touchAction: 'none', 
              cursor: isDraggingField ? 'grabbing' : 'crosshair' 
            }}
          />

          {/* Color field indicator */}
          <div
            className={styles.colorFieldIndicator}
            style={{ 
              left: `${fieldXPercent}%`, 
              top: `${fieldYPercent}%` 
            }}
          >
            <div className={styles.colorFieldCircle}>
              <div
                className={styles.colorFieldCircleInner}
                style={{ backgroundColor: hslToHex(hue, currentSaturation, currentLightness) }}
              />
            </div>
          </div>
        </div>

        {/* Hue Slider */}
        <div className={styles.hueSliderWrapper}>
          <canvas
            ref={hueCanvasRef}
            onPointerDown={handleHuePointerDown}
            onPointerMove={handleHuePointerMove}
            onPointerUp={handleHuePointerUp}
            className={styles.hueSliderCanvas}
            style={{
              touchAction: 'none',
              cursor: isDraggingHue ? 'grabbing' : 'pointer'
            }}
          />
          {/* Hue slider indicator */}
          <div
            className={styles.hueSliderIndicator}
            style={{ top: `${hueYPercent}%` }}
          >
            <div className={styles.hueSliderBar} />
          </div>
        </div>
      </div>

      {/* Alpha (opacity) Slider — horizontal, full-width. Suppressed when
          consumer passes showAlpha={false} (e.g. password-gate panel where
          the backdrop must always be fully opaque). */}
      {showAlpha ? (
        <div className={styles.alphaSliderWrapper}>
          <canvas
            ref={alphaCanvasRef}
            onPointerDown={handleAlphaPointerDown}
            onPointerMove={handleAlphaPointerMove}
            onPointerUp={handleAlphaPointerUp}
            className={styles.alphaSliderCanvas}
            style={{
              touchAction: 'none',
              cursor: isDraggingAlpha ? 'grabbing' : 'pointer',
            }}
          />
          <div
            className={styles.alphaSliderIndicator}
            style={{ left: `${effectiveAlpha * 100}%` }}
          >
            <div className={styles.alphaSliderBar} />
          </div>
        </div>
      ) : null}

      {/* Input field with format dropdown */}
      <div ref={inputWrapperRef} className={styles.inputWrapper}>
        <div className={`${styles.inputContainer} ${isDropdownOpen ? styles.inputContainerDropdownOpen : styles.inputContainerDropdownClosed}`}>
          <div className={`${styles.dropdownButtonWrapper} ${isDropdownOpen ? styles.dropdownButtonWrapperDropdownOpen : styles.dropdownButtonWrapperDropdownClosed}`}>
            <button
              ref={dropdownButtonRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={styles.dropdownButton}
            >
              {colorFormat}
              <svg className={styles.dropdownIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          <div ref={dividerRef} className={styles.divider} />
          
          <input
            type="text"
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
            className={`${styles.colorInput} ${isDropdownOpen ? styles.colorInputDropdownOpen : styles.colorInputDropdownClosed}`}
            placeholder={colorFormat === "HEX" ? "#FF6702" : "255, 103, 2"}
          />
        </div>
        
        {/* Dropdown - moved outside inputContainer to avoid overflow clipping */}
        {isDropdownOpen && (
          <>
            <div 
              className={styles.dropdownBackdrop}
              onClick={() => setIsDropdownOpen(false)}
            />
            <div ref={dropdownMenuRef} className={styles.dropdownMenu}>
              <button
                onClick={() => {
                  setColorFormat(colorFormat === "HEX" ? "RGB" : "HEX");
                  setIsDropdownOpen(false);
                }}
                className={styles.dropdownMenuItem}
              >
                {colorFormat === "HEX" ? "RGB" : "HEX"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Helper functions
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    red: parseInt(result[1], 16),
    green: parseInt(result[2], 16),
    blue: parseInt(result[3], 16)
  } : null;
}

function rgbToHex(red, green, blue) {
  const toHex = (num) => Math.max(0, Math.min(255, Math.round(num))).toString(16).padStart(2, "0");
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
}

function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return { hue: 0, saturation: 100, lightness: 50 };
  
  const redNorm = rgb.red / 255;
  const greenNorm = rgb.green / 255;
  const blueNorm = rgb.blue / 255;
  
  const max = Math.max(redNorm, greenNorm, blueNorm);
  const min = Math.min(redNorm, greenNorm, blueNorm);
  const delta = max - min;
  
  let hue = 0;
  let saturation = 0;
  let lightness = (max + min) / 2;
  
  if (delta !== 0) {
    saturation = lightness > 0.5 
      ? delta / (2 - max - min)
      : delta / (max + min);
    
    if (max === redNorm) {
      hue = ((greenNorm - blueNorm) / delta + (greenNorm < blueNorm ? 6 : 0)) * 60;
    } else if (max === greenNorm) {
      hue = ((blueNorm - redNorm) / delta + 2) * 60;
    } else {
      hue = ((redNorm - greenNorm) / delta + 4) * 60;
    }
  }
  
  return {
    hue: Math.round(hue),
    saturation: Math.round(saturation * 100),
    lightness: Math.round(lightness * 100)
  };
}

function hslToRgb(hue, saturation, lightness) {
  const hueNorm = hue / 360;
  const satNorm = saturation / 100;
  const lightNorm = lightness / 100;
  
  const hueToRgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let red, green, blue;
  
  if (satNorm === 0) {
    red = green = blue = lightNorm;
  } else {
    const q = lightNorm < 0.5 
      ? lightNorm * (1 + satNorm)
      : lightNorm + satNorm - lightNorm * satNorm;
    const p = 2 * lightNorm - q;
    
    red = hueToRgb(p, q, hueNorm + 1/3);
    green = hueToRgb(p, q, hueNorm);
    blue = hueToRgb(p, q, hueNorm - 1/3);
  }
  
  return {
    red: Math.round(red * 255),
    green: Math.round(green * 255),
    blue: Math.round(blue * 255)
  };
}

function hslToHex(hue, saturation, lightness) {
  const rgb = hslToRgb(hue, saturation, lightness);
  return rgbToHex(rgb.red, rgb.green, rgb.blue);
}
